import { useEffect, useState } from 'react';
import { useMeta } from '../state/MetaContext.jsx';
import { formatXaf } from '../lib/format.js';

// Price rungs that match how rents are actually advertised here: cheap rooms at
// the bottom, expatriate apartments at the top.
const PRICE_STEPS = [0, 15000, 25000, 40000, 60000, 80000, 120000, 200000, 350000, 600000, 1000000];

// And what houses and plots change hands for: a village plot to a Bonapriso villa.
const SALE_STEPS = [0, 1e6, 3e6, 5e6, 10e6, 20e6, 35e6, 50e6, 100e6, 250e6, 500e6, 1e9];

const priceOptions = (label, sale = false) =>
  (sale ? SALE_STEPS : PRICE_STEPS).map((value) => ({
    value: value === 0 ? '' : String(value),
    label: value === 0 ? label : formatXaf(value)
  }));

/** The single-line search bar used on the home page hero. */
export const HeroSearch = ({ onSearch }) => {
  const { cities } = useMeta();
  const [term, setTerm] = useState('');
  const [city, setCity] = useState('');

  return (
    <form
      className="hero-search"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch({ q: term.trim() || undefined, city: city || undefined });
      }}
    >
      <div className="hero-search-fields">
        <label className="hero-search-city">
          <span className="sr-only">Town</span>
          <select value={city} onChange={(event) => setCity(event.target.value)}>
            <option value="">Any town</option>
            {cities.map((item) => (
              <option key={item.name} value={item.name}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="hero-search-term">
          <span className="sr-only">What are you looking for</span>
          <input
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Neighbourhood, landmark or keyword, for example Molyko or two bedroom"
          />
        </label>
      </div>
      <button type="submit" className="btn btn-primary btn-lg">
        Search
      </button>
    </form>
  );
};

/**
 * Full filter panel for the search page. It keeps its own draft state and only
 * calls onChange when the visitor applies, so the results list is not refetched
 * on every keystroke.
 */
export const FilterPanel = ({ value, onChange, onReset, resultCount }) => {
  const { cities, regions, propertyTypes, amenities } = useMeta();
  const [draft, setDraft] = useState(value);
  const sale = value.kind === 'sale';
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);

  // Keep the draft in step when filters change from outside, for example when a
  // visitor arrives on a shared URL or clears everything.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const set = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const selectedAmenities = draft.amenities || [];
  const toggleAmenity = (slug) =>
    set({
      amenities: selectedAmenities.includes(slug)
        ? selectedAmenities.filter((item) => item !== slug)
        : [...selectedAmenities, slug]
    });

  const apply = (event) => {
    event.preventDefault();
    onChange({ ...draft, page: 1 });
  };

  return (
    <form className="filter-panel" onSubmit={apply}>
      <div className="filter-row">
        <label className="filter-grow">
          <span>Keyword</span>
          <input
            type="search"
            value={draft.q || ''}
            onChange={(event) => set({ q: event.target.value })}
            placeholder="Molyko, tarred road, self contained"
          />
        </label>

        <label>
          <span>Town</span>
          <select value={draft.city || ''} onChange={(event) => set({ city: event.target.value, region: '' })}>
            <option value="">Any town</option>
            {cities.map((city) => (
              <option key={city.name} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Region</span>
          <select value={draft.region || ''} onChange={(event) => set({ region: event.target.value, city: '' })}>
            <option value="">Any region</option>
            {regions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="filter-row">
        <label>
          <span>{sale ? 'Price from' : 'Rent from'}</span>
          <select value={draft.minPrice || ''} onChange={(event) => set({ minPrice: event.target.value })}>
            {priceOptions('No minimum', sale).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>{sale ? 'Price up to' : 'Rent up to'}</span>
          <select value={draft.maxPrice || ''} onChange={(event) => set({ maxPrice: event.target.value })}>
            {priceOptions('No maximum', sale).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Rooms</span>
          <select value={draft.rooms || ''} onChange={(event) => set({ rooms: event.target.value })}>
            <option value="">Any</option>
            {[1, 2, 3, 4, 5].map((count) => (
              <option key={count} value={count}>
                {count}+
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Type</span>
          <select value={draft.type || ''} onChange={(event) => set({ type: event.target.value })}>
            <option value="">Any type</option>
            {propertyTypes.map((type) => (
              <option key={type.slug} value={type.slug}>
                {type.label}
              </option>
            ))}
          </select>
        </label>

        {sale ? null : (
          <label>
            <span>How it is let</span>
            <select value={draft.letting || ''} onChange={(event) => set({ letting: event.target.value })}>
              <option value="">Any</option>
              <option value="whole">A whole house or flat</option>
              <option value="units">An apartment in a building</option>
              <option value="rooms">A room in a house</option>
            </select>
          </label>
        )}

        <label>
          <span>Availability</span>
          <select value={draft.available || ''} onChange={(event) => set({ available: event.target.value })}>
            <option value="">{sale ? 'Everything, sold too' : 'Everything, rented too'}</option>
            <option value="true">{sale ? 'Still for sale only' : 'Available now only'}</option>
          </select>
        </label>

        <label>
          <span>Furnishing</span>
          <select value={draft.furnished || ''} onChange={(event) => set({ furnished: event.target.value })}>
            <option value="">Either</option>
            <option value="true">Furnished</option>
            <option value="false">Unfurnished</option>
          </select>
        </label>
      </div>

      <div className="filter-amenities">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setAmenitiesOpen((open) => !open)}
          aria-expanded={amenitiesOpen}
        >
          {amenitiesOpen ? 'Hide' : 'Show'} amenities
          {selectedAmenities.length > 0 ? ` (${selectedAmenities.length} selected)` : ''}
        </button>

        {amenitiesOpen ? (
          <div className="amenity-grid">
            {amenities.map((amenity) => (
              <label key={amenity.slug} className="check">
                <input
                  type="checkbox"
                  checked={selectedAmenities.includes(amenity.slug)}
                  onChange={() => toggleAmenity(amenity.slug)}
                />
                <span>{amenity.label}</span>
              </label>
            ))}
          </div>
        ) : null}
      </div>

      <div className="filter-actions">
        <button type="submit" className="btn btn-primary">
          Apply filters
        </button>
        <button type="button" className="btn btn-ghost" onClick={onReset}>
          Clear all
        </button>
        {typeof resultCount === 'number' ? (
          <span className="filter-count">
            {resultCount} {resultCount === 1 ? 'listing' : 'listings'} match
          </span>
        ) : null}
      </div>
    </form>
  );
};
